import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "netzer-studio-theme-v1";
const THEME_VERSION = 2;
const DEFAULT_THEME = { mode: "dark", accent: "#8CC63F", fontScale: 1.1 };
const ThemeContext = createContext(null);

function normalizeHex(value) {
  const candidate = String(value || "").trim();
  return /^#[0-9a-f]{6}$/i.test(candidate) ? candidate.toUpperCase() : DEFAULT_THEME.accent;
}

function hexToRgb(hex) {
  const clean = normalizeHex(hex).slice(1);
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  const channel = (value) => Math.round(Math.max(0, Math.min(255, value))).toString(16).padStart(2, "0");
  return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase();
}

function mix(hex, target, amount) {
  const source = hexToRgb(hex);
  return rgbToHex({
    r: source.r + (target.r - source.r) * amount,
    g: source.g + (target.g - source.g) * amount,
    b: source.b + (target.b - source.b) * amount,
  });
}

function rgbToHsl({ r, g, b }) {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  let hue = 0;
  let saturation = 0;
  const lightness = (max + min) / 2;
  const delta = max - min;

  if (delta) {
    saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    if (max === red) hue = (green - blue) / delta + (green < blue ? 6 : 0);
    if (max === green) hue = (blue - red) / delta + 2;
    if (max === blue) hue = (red - green) / delta + 4;
    hue /= 6;
  }

  return `${Math.round(hue * 360)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`;
}

function readableText(hex) {
  const { r, g, b } = hexToRgb(hex);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.58 ? "#09090B" : "#FFFFFF";
}

function readStoredTheme() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    const savedScale = Number(saved?.fontScale) || DEFAULT_THEME.fontScale;
    return {
      mode: saved?.mode === "light" ? "light" : "dark",
      accent: normalizeHex(saved?.accent),
      fontScale: saved?.version === THEME_VERSION
        ? Math.max(1, Math.min(1.25, savedScale))
        : Math.max(DEFAULT_THEME.fontScale, Math.min(1.25, savedScale)),
    };
  } catch {
    return DEFAULT_THEME;
  }
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(readStoredTheme);

  useEffect(() => {
    const root = document.documentElement;
    const accent = normalizeHex(theme.accent);
    const contrast = readableText(accent);
    root.dataset.theme = theme.mode;
    root.style.fontSize = `${16 * theme.fontScale}px`;
    root.style.setProperty("--studio-primary", accent);
    root.style.setProperty("--studio-primary-hover", mix(accent, { r: 255, g: 255, b: 255 }, 0.2));
    root.style.setProperty("--studio-primary-deep", mix(accent, { r: 0, g: 94, b: 84 }, 0.35));
    root.style.setProperty("--studio-primary-contrast", contrast);
    root.style.setProperty("--primary", rgbToHsl(hexToRgb(accent)));
    root.style.setProperty("--ring", rgbToHsl(hexToRgb(accent)));
    root.style.setProperty("--primary-foreground", rgbToHsl(hexToRgb(contrast)));
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...theme, accent, version: THEME_VERSION }));
  }, [theme]);

  const setMode = useCallback((mode) => {
    setTheme((current) => ({ ...current, mode: mode === "light" ? "light" : "dark" }));
  }, []);

  const toggleMode = useCallback(() => {
    setTheme((current) => ({ ...current, mode: current.mode === "dark" ? "light" : "dark" }));
  }, []);

  const setAccent = useCallback((accent) => {
    setTheme((current) => ({ ...current, accent: normalizeHex(accent) }));
  }, []);

  const setFontScale = useCallback((fontScale) => {
    setTheme((current) => ({ ...current, fontScale: Math.max(1, Math.min(1.25, Number(fontScale) || DEFAULT_THEME.fontScale)) }));
  }, []);

  const resetTheme = useCallback(() => setTheme(DEFAULT_THEME), []);
  const value = useMemo(() => ({ ...theme, setMode, toggleMode, setAccent, setFontScale, resetTheme }), [theme, setMode, toggleMode, setAccent, setFontScale, resetTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme precisa ser usado dentro de ThemeProvider");
  return context;
}
